import { useLogin } from 'hooks/useLogin';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, Stack, styled } from '@mui/material';
import { useTheme } from '@mui/system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton';
import { InitPageContainer } from 'components/views/InitPageContainer/InitPageContainer';
import { RoutePaths } from 'constants/RoutePaths';
import { StyledErrorDiv } from "@theme/styledComponents";

export const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { login, loginPending } = useLogin();

    const { t } = useTranslation();
    const theme = useTheme();
  
    const validateLogin = () => {
      if (email === '') {
        setLoginError(t('loginPage.errors.loginRequired'));
        return false;
      }
      setLoginError('');
      return true;
    };
  
    const validatePassword = () => {
      if (password === '') {
        setPasswordError(t('loginPage.errors.passwordRequired'));
        return false;
      }
      setPasswordError('');
      return true;
    };
  
    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = (event: any) => event.preventDefault();
  
    const handleLogin = async () => {
      const isLoginValid = validateLogin();
      const isPasswordValid = validatePassword();
      
      if (isLoginValid && isPasswordValid) {
        await login({
            email: email,
            password: password
        });     
      }
    };
  
    return (
      <InitPageContainer 
        title={t('loginPage.logIn')} 
        footerText={t('loginPage.loginProblem')} 
        footerUrl={RoutePaths.FORGOT_PASSWORD} 
        cardHeight='540px'>

        <Stack spacing={theme.spacing(4)} width={'100%'} display='flex' justifyContent={'center'}>
            <FormControl variant="outlined">
              <InputLabel htmlFor="email">{t('loginPage.email')}</InputLabel>
              <OutlinedInput
                id="email"
                label={t('loginPage.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={validateLogin}
                inputProps={{ "data-testid": "login-email-input" }}
              />
              {loginError && <StyledErrorDiv>{loginError}</StyledErrorDiv>}
            </FormControl>
            <FormControl variant="outlined">
              <InputLabel htmlFor="password">{t('loginPage.password')}</InputLabel>
              <OutlinedInput
                id="password"
                label={t('loginPage.password')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={validatePassword}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{"data-testid": "login-password-input"}}
              />
              {passwordError && <StyledErrorDiv>{passwordError}</StyledErrorDiv>}
            </FormControl>
            <LoadingButton
              data-testid="login-submit-button" 
              loading={loginPending} 
              variant='contained' 
              color={theme.palette.main} 
              onClick={handleLogin}>
                {t('loginPage.loginButtonText')}
            </LoadingButton>
        </Stack>
        </InitPageContainer>
    );
  };